import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import LoadingSpinner from '../../components/LoadingSpinner'
import { publicApiCall } from '../../services/api'
import { colors } from '../../theme'
import BrandLogo from '../../components/BrandLogo'
import LegalFooter from '../../components/LegalFooter'

export default function SetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [token, setToken] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const tokenParam = searchParams.get('token')
    if (!tokenParam) {
      setError('Invalid or missing verification link')
    } else {
      setToken(tokenParam)
    }
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate password
    if (password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter')
      return
    }

    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter')
      return
    }

    // Check for number
    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number')
      return
    }

    // Check for symbol
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      setError('Password must contain at least one symbol (!@#$%^&* etc.)')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      await publicApiCall('set-password', {
        method: 'POST',
        body: { token, password },
      })

      setSuccess(true)
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Password set successfully! Please log in.' },
        })
      }, 2000)
    } catch (err) {
      console.error('Error setting password:', err)
      setError(err instanceof Error ? err.message : 'Failed to set password')
    } finally {
      setLoading(false)
    }
  }

  if (!token && !error) {
    return <LoadingSpinner />
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      flexDirection="column"
      alignItems="center"
      minHeight="100vh"
      sx={{
        background: `linear-gradient(135deg, ${colors.gray900} 0%, ${colors.gray700} 100%)`,
        padding: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 2, border: `1px solid ${colors.gray200}` }}>
        <CardContent sx={{ padding: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            {/* Lockup carries the wordmark, so the name is not repeated below it */}
            <BrandLogo height={44} sx={{ mb: 2 }} />
          </Box>
          <Typography variant="h5" gutterBottom sx={{ color: colors.gray800, fontWeight: 600 }}>
            Set Your Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a password for your MissFit Mindset account.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Password set successfully! Redirecting to login...
            </Alert>
          )}

          {!success && token && (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                helperText="Must contain: 8+ characters, uppercase, lowercase, number, and symbol"
              />
              <TextField
                fullWidth
                type="password"
                label="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Set Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <LegalFooter tone="onDark" sx={{ mt: 2 }} />
    </Box>
  )
}
